/**
 * @file 学员新建 / 编辑弹窗
 * @description 基础信息 + 按科目动态详情（至少一条）。科目详情级联交互：
 * 先选科目 → 按科目调 `/educate/textbook/ddl-pc` 取教材 → 选中教材后按其
 * 适用配置派生 阶段 / 年级 / 学期 / 学习力等级 选项（未选教材前禁用）。
 * 教师下拉取 `/pc/teacher/ddl`。
 *
 * @whenToUse 学员管理页创建与编辑。
 * @whenNotToUse 绑定教师、充值等其他操作弹窗。
 */

import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { fetchTeacherDdl, type TeacherItem } from '@/api/teacher.ts'
import { fetchTextbookDdl, splitCsv, type TextbookDdlItem } from '@/api/textbook.ts'
import type { StudentItem } from '@/api/student.ts'
import {
  educationStageMap,
  gradeMap,
  learningLevelMap,
  semesterMap,
  Grade,
} from '@/enum/common.ts'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { IconButton } from '@/components/ui/icon-button.tsx'
import { Input } from '@/components/ui/input.tsx'
import { InputNumber } from '@/components/ui/input-number.tsx'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select } from '@/components/ui/select.tsx'
import {
  EMPTY_STUDENT_FORM,
  SUBJECT_OPTIONS,
  createEmptyDetailForm,
  type StudentDetailFormValues,
  type StudentFormValues,
} from '@/components/pages/student-page/types.ts'

const PHONE_PATTERN = /^1\d{10}$/

type FormErrors = {
  studentName?: string
  phone?: string
  points?: string
  password?: string
  details?: string
  detailFields?: Partial<
    Record<keyof Omit<StudentDetailFormValues, 'key' | 'id'>, string>
  >[]
}

/**
 * 从教材配置中派生级联选项：逐级按已选值过滤配置行，再取目标字段的值集合
 * @param textbook 已选教材
 * @param picked 已选的阶段 / 年级 / 学期
 * @param field 要派生的字段
 * @param labelMap 枚举值 → 展示文案
 */
function deriveConfigOptions(
  textbook: TextbookDdlItem | undefined,
  picked: { educationStage?: string; grade?: string; semester?: string },
  field: 'educationStage' | 'grade' | 'semester' | 'learningGrade',
  labelMap: Record<string, string>,
): { value: string; label: string }[] {
  if (!textbook) return []
  const rows = textbook.textbookConfigList.filter((row) => {
    if (
      picked.educationStage &&
      !splitCsv(row.educationStage).includes(picked.educationStage)
    ) {
      return false
    }
    if (picked.grade && !splitCsv(row.grade).includes(picked.grade)) {
      return false
    }
    if (picked.semester && !splitCsv(row.semester).includes(picked.semester)) {
      return false
    }
    return true
  })
  const values = Array.from(
    new Set(rows.flatMap((row) => splitCsv(row[field]))),
  )
  return values.map((value) => ({ value, label: labelMap[value] ?? value }))
}

/**
 * 科目详情卡：承载 科目 → 教材 → 配置项 级联与各自禁用态
 */
function DetailCard({
  detail,
  index,
  fieldErrors,
  usedSubjects,
  canRemove,
  onChange,
  onRemove,
}: {
  detail: StudentDetailFormValues
  index: number
  fieldErrors: Partial<Record<keyof Omit<StudentDetailFormValues, 'key' | 'id'>, string>>
  usedSubjects: string[]
  canRemove: boolean
  onChange: (patch: Partial<StudentDetailFormValues>) => void
  onRemove: () => void
}) {
  const [textbooks, setTextbooks] = useState<TextbookDdlItem[]>([])
  const [loadingTextbooks, setLoadingTextbooks] = useState(false)
  const [teachers, setTeachers] = useState<TeacherItem[]>([])

  useEffect(() => {
    let active = true
    if (!detail.subject) {
      setTextbooks([])
      return
    }
    setLoadingTextbooks(true)
    void fetchTextbookDdl({ subject: detail.subject })
      .then((items) => {
        if (active) setTextbooks(items)
      })
      .catch(() => {
        if (active) setTextbooks([])
      })
      .finally(() => {
        if (active) setLoadingTextbooks(false)
      })

    // 教师下拉按科目过滤（/pc/teacher/ddl 支持 subject 参数）
    void fetchTeacherDdl({ subjects: detail.subject })
      .then((items) => {
        if (active) setTeachers(items)
      })
      .catch(() => {
        if (active) setTeachers([])
      })
    return () => {
      active = false
    }
  }, [detail.subject])

  const textbook = textbooks.find((item) => item.id === detail.textbookId)
  const textbookChosen = Boolean(textbook)

  const stageOptions = useMemo(
    () =>
      deriveConfigOptions(
        textbook,
        { grade: detail.grade, semester: detail.semester },
        'educationStage',
        educationStageMap,
      ),
    [textbook, detail.grade, detail.semester],
  )
  const gradeOptions = useMemo(
    () =>
      deriveConfigOptions(
        textbook,
        { educationStage: detail.educationStage, semester: detail.semester },
        'grade',
        gradeMap,
      ),
    [textbook, detail.educationStage, detail.semester],
  )
  const semesterOptions = useMemo(
    () =>
      deriveConfigOptions(
        textbook,
        { educationStage: detail.educationStage, grade: detail.grade },
        'semester',
        semesterMap,
      ),
    [textbook, detail.educationStage, detail.grade],
  )
  const learningGradeOptions = useMemo(
    () =>
      deriveConfigOptions(
        textbook,
        {
          educationStage: detail.educationStage,
          grade: detail.grade,
          semester: detail.semester,
        },
        'learningGrade',
        learningLevelMap,
      ),
    [textbook, detail.educationStage, detail.grade, detail.semester],
  )

  /** 年级为「通用」时不区分学期：隐藏学期项并置空（含编辑回显的旧值） */
  const isCommonGrade = detail.grade === Grade.COMMON
  useEffect(() => {
    if (isCommonGrade && detail.semester) onChange({ semester: '' })
    // 仅在年级切换为通用时执行清空
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 见上
  }, [isCommonGrade])

  /** 切换科目后清空教材、全部配置项与教师（教师选项按科目过滤） */
  const changeSubject = (subject: string) => {
    onChange({
      subject,
      textbookId: '',
      educationStage: '',
      grade: '',
      semester: '',
      learningGrade: '',
      teacherId: '',
    })
  }

  /** 切换教材后清空配置项（learningForce 保留手输值） */
  const changeTextbook = (textbookId: string) => {
    onChange({
      textbookId,
      educationStage: '',
      grade: '',
      semester: '',
      learningGrade: '',
    })
  }

  const subjectOptions = SUBJECT_OPTIONS.map((option) => ({
    ...option,
    disabled:
      option.value !== detail.subject && usedSubjects.includes(option.value),
  }))

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-foreground">详情 {index + 1}</p>
        <IconButton
          variant="ghost"
          aria-label={`删除详情 ${index + 1}`}
          disabled={!canRemove}
          icon={<Trash2 className="size-4" aria-hidden />}
          onClick={onRemove}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="科目" required error={fieldErrors.subject}>
          <Select
            value={detail.subject || undefined}
            invalid={Boolean(fieldErrors.subject)}
            placeholder="请先选择科目"
            options={subjectOptions}
            onChange={changeSubject}
          />
        </FormField>
        <FormField label="教材" required error={fieldErrors.textbookId}>
          <Select
            value={detail.textbookId || undefined}
            invalid={Boolean(fieldErrors.textbookId)}
            placeholder={detail.subject ? '请选择教材' : '请先选择科目'}
            disabled={!detail.subject || loadingTextbooks}
            options={textbooks.map((item) => ({ value: item.id, label: item.name }))}
            onChange={changeTextbook}
          />
        </FormField>
        <FormField label="阶段" required error={fieldErrors.educationStage}>
          <Select
            value={detail.educationStage || undefined}
            invalid={Boolean(fieldErrors.educationStage)}
            placeholder={textbookChosen ? '请选择教育阶段' : '请先选择教材'}
            disabled={!textbookChosen}
            options={stageOptions}
            onChange={(value) =>
              onChange({ educationStage: value, grade: '', semester: '', learningGrade: '' })
            }
          />
        </FormField>
        <FormField label="年级" required error={fieldErrors.grade}>
          <Select
            value={detail.grade || undefined}
            invalid={Boolean(fieldErrors.grade)}
            placeholder={textbookChosen ? '请选择年级' : '请先选择教材'}
            disabled={!textbookChosen}
            options={gradeOptions}
            onChange={(value) => onChange({ grade: value, semester: '', learningGrade: '' })}
          />
        </FormField>
        {isCommonGrade ? null : (
          <FormField label="学期" required error={fieldErrors.semester}>
            <Select
              value={detail.semester || undefined}
              invalid={Boolean(fieldErrors.semester)}
              placeholder={textbookChosen ? '请选择学期' : '请先选择教材'}
              disabled={!textbookChosen}
              options={semesterOptions}
              onChange={(value) => onChange({ semester: value, learningGrade: '' })}
            />
          </FormField>
        )}
        <FormField label="学习力等级" required error={fieldErrors.learningGrade}>
          <Select
            value={detail.learningGrade || undefined}
            invalid={Boolean(fieldErrors.learningGrade)}
            placeholder={textbookChosen ? '请选择学习力等级' : '请先选择教材'}
            disabled={!textbookChosen}
            options={learningGradeOptions}
            onChange={(value) => onChange({ learningGrade: value })}
          />
        </FormField>
        <FormField label="学习力" required error={fieldErrors.learningForce}>
          <InputNumber
            value={detail.learningForce}
            min={0}
            step={5}
            precision={0}
            invalid={Boolean(fieldErrors.learningForce)}
            onChange={(value) => onChange({ learningForce: value ?? 0 })}
            onBlur={() => onChange({ learningForce: Math.round(detail.learningForce / 5) * 5 })}
          />
        </FormField>
        <FormField label="所属教师" hint="可留空，之后绑定">
          <Select
            value={detail.teacherId || undefined}
            placeholder={detail.subject ? '请选择教师（可选）' : '请先选择科目'}
            disabled={!detail.subject}
            options={teachers.map((teacher) => ({
              value: teacher.id,
              label: teacher.teacherName,
            }))}
            onChange={(value) => onChange({ teacherId: value })}
          />
        </FormField>
      </div>
    </div>
  )
}

export type StudentFormDialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 编辑目标；为空表示新建 */
  editing: StudentItem | null
  /** 提交 */
  onSubmit: (values: StudentFormValues) => Promise<void>
}

/**
 * 学员创建 / 编辑 Dialog
 */
export function StudentFormDialog({
  open,
  onOpenChange,
  editing,
  onSubmit,
}: StudentFormDialogProps) {
  const isEdit = Boolean(editing)
  const [values, setValues] = useState<StudentFormValues>(EMPTY_STUDENT_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    if (editing) {
      setValues({
        studentName: editing.studentName,
        phone: editing.phone,
        points: editing.points,
        password: '',
        details: editing.subjectList.map((item) => ({
          id: item.id,
          key: item.id,
          subject: item.subject,
          textbookId: item.textbookId,
          grade: item.grade,
          semester: item.semester,
          educationStage: item.educationStage,
          learningGrade: item.learningGrade,
          learningForce: item.learningForce,
          teacherId: item.teacherId,
        })),
      })
    } else {
      setValues({
        ...EMPTY_STUDENT_FORM,
        details: [createEmptyDetailForm()],
      })
    }
    setErrors({})
  }, [open, editing])

  const setBaseField = <K extends 'studentName' | 'phone' | 'points' | 'password'>(
    key: K,
    value: StudentFormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const patchDetail = (
    index: number,
    patch: Partial<StudentDetailFormValues>,
  ) => {
    setValues((prev) => ({
      ...prev,
      details: prev.details.map((item, i) =>
        i === index ? { ...item, ...patch } : item,
      ),
    }))
  }

  const addDetail = () => {
    setValues((prev) => ({
      ...prev,
      details: [...prev.details, createEmptyDetailForm()],
    }))
  }

  const removeDetail = (index: number) => {
    if (values.details.length <= 1) return
    setValues((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }))
  }

  const validate = () => {
    const next: FormErrors = { detailFields: [] }
    if (!values.studentName.trim()) next.studentName = '请输入学员名称'
    const phone = values.phone.trim()
    if (!phone) next.phone = '请输入手机号'
    else if (!PHONE_PATTERN.test(phone)) next.phone = '请输入 11 位手机号'
    if (values.points < 0) next.points = '点数不能为负'
    if (!isEdit && values.password.trim() && values.password.length < 6)
      next.password = '密码至少 6 位'
    if (values.details.length === 0) next.details = '至少保留一条科目详情'

    const subjects = values.details.map((item) => item.subject).filter(Boolean)
    const duplicated = subjects.some(
      (subject, index) => subjects.indexOf(subject) !== index,
    )
    if (duplicated) next.details = '同一学员下科目不能重复'

    next.detailFields = values.details.map((item) => {
      const field: NonNullable<FormErrors['detailFields']>[number] = {}
      if (!item.subject) field.subject = '请选择科目'
      if (!item.textbookId) field.textbookId = '请选择教材'
      if (!item.grade) field.grade = '请选择年级'
      // 「通用」年级不区分学期，学期不选也不拦截
      if (item.grade !== Grade.COMMON && !item.semester)
        field.semester = '请选择学期'
      if (!item.educationStage) field.educationStage = '请选择教育阶段'
      if (!item.learningGrade) field.learningGrade = '请选择学习力等级'
      if (item.learningForce < 0) field.learningForce = '学习力不能为负'
      return field
    })

    const hasDetailError = next.detailFields.some(
      (field) => Object.keys(field).length > 0,
    )
    setErrors(next)
    return (
      !next.studentName &&
      !next.phone &&
      !next.points &&
      !next.password &&
      !next.details &&
      !hasDetailError
    )
  }

  const handleSubmit = async () => {
    if (!validate() || submitting) return
    setSubmitting(true)
    try {
      await onSubmit({
        ...values,
        studentName: values.studentName.trim(),
        phone: values.phone.trim(),
      })
      onOpenChange(false)
    } finally {
      setSubmitting(false)
    }
  }

  const usedSubjects = values.details.map((item) => item.subject)

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? '编辑学员' : '新增学员'}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button loading={submitting} onClick={handleSubmit}>
            {isEdit ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-3">
          <FormField label="学员名称" required error={errors.studentName}>
            <Input
              value={values.studentName}
              invalid={Boolean(errors.studentName)}
              placeholder="请输入学员名称"
              onChange={(event) =>
                setBaseField('studentName', event.target.value)
              }
            />
          </FormField>
          <FormField
            label="手机号"
            required
            error={errors.phone}
            hint="登录账号"
          >
            <Input
              value={values.phone}
              invalid={Boolean(errors.phone)}
              placeholder="请输入手机号"
              inputMode="numeric"
              maxLength={11}
              onChange={(event) =>
                setBaseField(
                  'phone',
                  event.target.value.replace(/\D/g, '').slice(0, 11),
                )
              }
            />
          </FormField>
          <FormField label="点数" required error={errors.points}>
            <InputNumber
              value={values.points}
              min={0}
              invalid={Boolean(errors.points)}
              onChange={(value) => setBaseField('points', value ?? 0)}
            />
          </FormField>
          {!isEdit ? (
            <FormField
              label="登录密码"
              error={errors.password}
              hint="非必填，留空则默认 123456"
            >
              <Input
                type="password"
                value={values.password}
                invalid={Boolean(errors.password)}
                placeholder="请输入密码（默认123456）"
                autoComplete="new-password"
                onChange={(event) =>
                  setBaseField('password', event.target.value)
                }
              />
            </FormField>
          ) : null}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium text-foreground">科目详情</p>
              {errors.details ? (
                <span className="ml-2 text-danger">{errors.details}</span>
              ) : null}
            </div>
            <Button
              type="button"
              variant="secondary"
              icon={<Plus className="size-4" aria-hidden />}
              onClick={addDetail}
            >
              添加详情信息
            </Button>
          </div>

          <ScrollArea className="h-128">
            <div className="space-y-3">
              {values.details.map((detail, index) => (
                <DetailCard
                  key={detail.key}
                  detail={detail}
                  index={index}
                  fieldErrors={errors.detailFields?.[index] ?? {}}
                  usedSubjects={usedSubjects}
                  canRemove={values.details.length > 1}
                  onChange={(patch) => patchDetail(index, patch)}
                  onRemove={() => removeDetail(index)}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </Dialog>
  )
}
